(() => {
  if (customElements.get('nerdtaverne-cart-merchandising')) return;

  const normalizeShopifyId = (value) => String(value || '').split('/').pop();
  const MAX_INLINE_BUNDLE_OFFERS = 3;

  class NerdtaverneCartMerchandising extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.loadRecommendations();
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
    }

    async loadRecommendations() {
      const productIds = [
        ...new Set(
          String(this.dataset.productIds || '')
            .split(',')
            .map(normalizeShopifyId)
            .filter(Boolean)
        ),
      ];

      if (!productIds.length) return;

      const limit = Math.max(1, Math.min(Number.parseInt(this.dataset.limit, 10) || 3, 3));
      const search = new URLSearchParams({
        product_ids: productIds.join(','),
        limit: String(limit),
      });

      try {
        const response = await fetch(`/apps/nerdtaverne/cart-recommendations?${search}`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Recommendation request failed with ${response.status}`);

        const payload = await response.json();
        const schemaVersion = Number(payload?.schemaVersion);
        if (!Number.isInteger(schemaVersion) || schemaVersion < 3) return;
        const bundleComponentIds = this.renderBundleAlternatives(payload.bundleAlternatives);
        const seenProducts = new Set();
        const curatedSource = Array.isArray(payload.curatedItems)
          ? payload.curatedItems.filter((item) => (
            item.recommendationClass === 'curated'
            && item.provenance === 'curated'
          ))
          : [];
        const copurchaseSource = Array.isArray(payload.copurchaseItems)
          ? payload.copurchaseItems.filter((item) => (
            item.recommendationClass === 'copurchase'
            && item.provenance === 'behavioral'
            && Number(item.copurchaseCount || 0) >= 2
          ))
          : [];
        const filterItems = (items) => items
          .filter((item) => {
            const productId = normalizeShopifyId(item && item.productId);
            const variantId = normalizeShopifyId(item && item.variantId);
            if (!productId || !variantId || productIds.includes(productId) || seenProducts.has(productId)) return false;
            if (bundleComponentIds.has(productId)) return false;
            if (item.available === false) return false;
            seenProducts.add(productId);
            return true;
          });
        const curatedItems = filterItems(curatedSource);
        const copurchaseItems = filterItems(copurchaseSource);
        let selectedCurated = curatedItems.slice(0, limit);
        let selectedCopurchases = [];
        if (curatedItems.length && copurchaseItems.length && limit >= 2) {
          selectedCurated = curatedItems.slice(0, limit - 1);
          selectedCopurchases = copurchaseItems.slice(0, 1);
          const remaining = limit - selectedCurated.length - selectedCopurchases.length;
          if (remaining > 0) {
            selectedCopurchases = selectedCopurchases.concat(
              copurchaseItems.slice(
                selectedCopurchases.length,
                selectedCopurchases.length + remaining
              )
            );
          }
        } else if (!curatedItems.length) {
          selectedCopurchases = copurchaseItems.slice(0, limit);
        }
        const items = [...selectedCurated, ...selectedCopurchases];

        this.renderItems(items);
        this.hidden = items.length === 0;
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.hidden = true;
        }
      }
    }

    renderItems(items) {
      const curatedItems = items.filter((item) => item.recommendationClass !== 'copurchase');
      const copurchaseItems = items.filter((item) => item.recommendationClass === 'copurchase');

      this.renderGroup('curated', curatedItems);
      this.renderGroup('copurchase', copurchaseItems);
    }

    renderGroup(groupName, items) {
      const group = this.querySelector(`[data-recommendation-group="${groupName}"]`);
      const list = this.querySelector(`[data-recommendation-items="${groupName}"]`);
      if (!group || !list) return;

      list.replaceChildren(...items.map((item) => this.createCard(item)));
      group.hidden = items.length === 0;
    }

    renderBundleAlternatives(rawAlternatives) {
      if (this.dataset.context !== 'page') return new Set();

      const cartItems = this.closest('cart-items') || document.querySelector('cart-items');
      if (!cartItems) return new Set();

      const slots = [...cartItems.querySelectorAll('[data-nt-cart-bundle-slot-row]')]
        .map((row, index) => {
          const slot = row.querySelector('[data-nt-cart-bundle-slot]');
          const lineKey = String(row.dataset.lineKey || '');

          return {
            row,
            slot,
            productId: normalizeShopifyId(row.dataset.productId),
            lineKey,
            unitPriceCents: Number(row.dataset.unitPriceCents),
            index,
          };
        })
        .filter((entry) => entry.slot && entry.productId);

      slots.forEach(({ row, slot }) => {
        slot.replaceChildren();
        row.hidden = true;
      });

      if (!Array.isArray(rawAlternatives) || !slots.length) return new Set();

      const offersByBundleId = new Map();
      rawAlternatives.forEach((rawOffer) => {
        const bundleId = normalizeShopifyId(rawOffer?.bundleProductId || rawOffer?.productId);
        const sourceProductIds = this.normalizeProductIds(rawOffer?.sourceProductIds);
        const savingsCents = this.bundleSavings(rawOffer);

        if (!bundleId || !sourceProductIds.length || rawOffer?.available === false || savingsCents <= 0) return;

        const existing = offersByBundleId.get(bundleId);
        const mergedSourceProductIds = this.normalizeProductIds([
          ...(existing?.sourceProductIds || []),
          ...sourceProductIds,
        ]);
        const candidate = {
          ...rawOffer,
          bundleId,
          sourceProductIds: mergedSourceProductIds,
          savingsCents,
        };

        if (!existing || candidate.savingsCents > existing.savingsCents) {
          offersByBundleId.set(bundleId, candidate);
        } else {
          existing.sourceProductIds = mergedSourceProductIds;
        }
      });

      const offersBySlot = new Map();
      offersByBundleId.forEach((offer) => {
        const matchingSlots = slots.filter((slot) => offer.sourceProductIds.includes(slot.productId));
        const anchor = matchingSlots[0];
        if (!anchor) return;

        const anchoredOffer = {
          ...offer,
          cartProductIds: this.normalizeProductIds(matchingSlots.map((slot) => slot.productId)),
          replacementLineKey: anchor.lineKey,
          replacementUnitPriceCents: anchor.unitPriceCents,
        };
        const group = offersBySlot.get(anchor.row) || [];
        group.push(anchoredOffer);
        offersBySlot.set(anchor.row, group);
      });

      const excludedProductIds = new Set();
      offersBySlot.forEach((offers, row) => {
        const slot = row.querySelector('[data-nt-cart-bundle-slot]');
        if (!slot) return;
        const sortedOffers = offers.sort((left, right) => (
          right.cartProductIds.length - left.cartProductIds.length
          || right.savingsCents - left.savingsCents
          || String(left.bundleId).localeCompare(String(right.bundleId))
        ));
        const visibleOffers = sortedOffers.slice(0, MAX_INLINE_BUNDLE_OFFERS);
        visibleOffers.forEach((offer) => {
          this.bundleComponents(offer)
            .map((component) => normalizeShopifyId(component?.productId))
            .filter(Boolean)
            .forEach((productId) => excludedProductIds.add(productId));
        });
        slot.append(this.createBundleOfferGroup(visibleOffers));
        row.hidden = false;
      });

      return excludedProductIds;
    }

    normalizeProductIds(values) {
      const source = Array.isArray(values) ? values : [values];
      return [...new Set(source.map(normalizeShopifyId).filter(Boolean))];
    }

    bundleComponents(offer) {
      return Array.isArray(offer?.components)
        ? offer.components
        : (Array.isArray(offer?.contains) ? offer.contains : []);
    }

    bundleSavings(offer) {
      const suppliedSavings = Number(offer?.savingsCents);
      if (Number.isFinite(suppliedSavings)) return Math.max(0, Math.round(suppliedSavings));

      const componentSubtotal = Number(offer?.componentSubtotalCents);
      const bundlePrice = this.itemPriceCents(offer);
      if (!Number.isFinite(componentSubtotal) || !Number.isFinite(bundlePrice)) return 0;
      return Math.max(0, Math.round(componentSubtotal - bundlePrice));
    }

    itemPriceCents(item) {
      const cents = Number(item?.priceCents ?? item?.bundlePriceCents);
      if (Number.isFinite(cents)) return Math.max(0, Math.round(cents));

      const fallback = String(item?.price ?? '').trim().replace(',', '.');
      if (!fallback) return NaN;
      const value = fallback.includes('.') ? Number.parseFloat(fallback) * 100 : Number(fallback);
      return Number.isFinite(value) ? Math.max(0, Math.round(value)) : NaN;
    }

    createBundleOfferGroup(offers) {
      const wrapper = document.createElement('section');
      const primaryOffer = offers[0];

      wrapper.className = 'nt-cart-line-bundles__content';
      wrapper.append(this.createBundleOfferCard(primaryOffer));

      if (offers.length > 1) {
        const more = document.createElement('details');
        const summary = document.createElement('summary');
        const list = document.createElement('div');
        const count = offers.length - 1;

        more.className = 'nt-cart-line-bundles__more';
        summary.textContent = `${count} weitere Bundle-${count === 1 ? 'Option' : 'Optionen'} ansehen`;
        list.className = 'nt-cart-line-bundles__more-list';
        list.append(...offers.slice(1).map((offer) => this.createBundleOfferCard(offer)));
        more.append(summary, list);
        wrapper.append(more);
      }

      return wrapper;
    }

    createBundleOfferCard(offer) {
      const card = document.createElement('article');
      const content = document.createElement('div');
      const details = document.createElement('div');
      const title = this.createProductLink(offer, 'nt-cart-line-bundles__product-title');
      const meta = document.createElement('p');
      const bundleVariantId = normalizeShopifyId(offer?.variantId);
      const action = bundleVariantId && offer?.replacementLineKey
        ? document.createElement('button')
        : this.createProductLink(offer, 'nt-cart-line-bundles__action', `Bundle ${String(offer.title || '')} ansehen`);
      const price = this.itemPriceCents(offer);
      const componentSubtotal = Number(offer.componentSubtotalCents);
      const savings = this.bundleSavings(offer);
      const components = this.bundleComponents(offer);
      const savingsPercentage = Number.isFinite(componentSubtotal) && componentSubtotal > 0
        ? Math.round((savings / componentSubtotal) * 100)
        : 0;
      const unitPrice = Number(offer.replacementUnitPriceCents);
      const upgradePrice = Number.isFinite(unitPrice) ? Math.max(0, price - unitPrice) : NaN;
      const additions = components.filter((component) => (
        !this.normalizeProductIds(offer.sourceProductIds).includes(normalizeShopifyId(component?.productId))
      ));
      const additionTitle = String(additions[0]?.title || '').trim();
      const additionLabel = /deck\s*box/i.test(additionTitle)
        ? 'Deckbox'
        : additionTitle || 'Passende Extras';

      card.className = 'nt-cart-line-bundles__card';
      content.className = 'nt-cart-line-bundles__card-content';
      details.className = 'nt-cart-line-bundles__details';
      title.textContent = Number.isFinite(upgradePrice) && upgradePrice > 0
        ? `${additionLabel} für ${this.formatCents(upgradePrice)} mehr ergänzen`
        : String(offer.title || 'Passendes Bundle');
      meta.className = 'nt-cart-line-bundles__meta';
      const bundlePrice = document.createElement('strong');
      bundlePrice.textContent = `Bundlepreis ${this.formatCents(price)}`;
      meta.append(bundlePrice);
      if (Number.isFinite(componentSubtotal) && componentSubtotal > price) {
        meta.append(` statt ${this.formatCents(componentSubtotal)}`);
        if (savingsPercentage > 0) {
          const saving = document.createElement('strong');
          saving.textContent = ` · ${savingsPercentage} % Rabatt`;
          meta.append(saving);
        }
      }
      action.className = 'nt-cart-line-bundles__action';
      if (action.tagName === 'BUTTON') {
        action.type = 'button';
        action.textContent = 'Zum Bundle wechseln';
        action.setAttribute(
          'aria-label',
          `${String(offer.title || 'Bundle')} statt dieses Artikels in den Warenkorb legen`
        );
        action.addEventListener('click', () => this.replaceCartLineWithBundle(offer, action));
      } else {
        action.textContent = 'Bundle ansehen';
      }

      details.append(title, meta);
      content.append(details, action);
      card.append(content);
      return card;
    }

    createCard(item) {
      const card = document.createElement('article');
      const media = this.createProductLink(
        item,
        'nt-cart-merchandising__media',
        `${String(item.title || 'Artikel')} ansehen`
      );
      const body = document.createElement('div');
      const kind = document.createElement('span');
      const title = this.createProductLink(item, 'nt-cart-merchandising__product-title');
      const reason = document.createElement('p');
      const actions = document.createElement('div');
      const price = document.createElement('span');
      const action = item.canQuickAdd === true
        ? document.createElement('button')
        : this.createProductLink(item, 'nt-cart-merchandising__add');

      card.className = 'nt-cart-merchandising__card';
      card.setAttribute('role', 'listitem');

      if (this.safeImageUrl(item.image)) {
        const image = document.createElement('img');
        image.className = 'nt-cart-merchandising__image';
        image.src = this.safeImageUrl(item.image);
        image.alt = '';
        image.loading = 'lazy';
        image.width = 144;
        image.height = 144;
        media.append(image);
      } else {
        media.textContent = '✦';
        if (media.tagName !== 'A') media.setAttribute('aria-hidden', 'true');
      }

      body.className = 'nt-cart-merchandising__body';
      kind.className = 'nt-cart-merchandising__kind';
      kind.textContent = this.kindLabel(item);
      title.textContent = String(item.title || 'Nerdiges Extra');
      reason.className = 'nt-cart-merchandising__reason';
      reason.textContent = String(item.reason || this.defaultReason(item));
      actions.className = 'nt-cart-merchandising__actions';
      price.className = 'nt-cart-merchandising__price';
      price.textContent = this.formatPrice(item);

      action.className = 'nt-cart-merchandising__add';
      if (action.tagName === 'BUTTON') {
        action.type = 'button';
        action.textContent = 'Hinzufügen';
        action.setAttribute(
          'aria-label',
          `${String(item.title || 'Artikel')} zum Warenkorb hinzufügen`
        );
        action.addEventListener('click', () => this.addToCart(item, action));
      } else {
        action.textContent = 'Optionen wählen';
        action.setAttribute(
          'aria-label',
          `Optionen für ${String(item.title || 'Artikel')} wählen`
        );
      }

      actions.append(price, action);
      body.append(kind, title, reason, actions);
      card.append(media, body);
      return card;
    }

    createProductLink(item, className, accessibleLabel = '') {
      const handle = String(item.handle || '').trim();
      const element = document.createElement(handle ? 'a' : 'span');
      element.className = className;
      if (handle) {
        element.href = `/products/${encodeURIComponent(handle)}`;
        if (accessibleLabel) element.setAttribute('aria-label', accessibleLabel);
      }
      return element;
    }

    safeImageUrl(value) {
      if (!value) return '';

      try {
        const url = new URL(String(value), window.location.origin);
        return ['http:', 'https:'].includes(url.protocol) ? url.href : '';
      } catch (_error) {
        return '';
      }
    }

    kindLabel(item) {
      if (item.recommendationClass === 'copurchase') return 'Gemeinsam gekauft';
      const labels = {
        bundle: 'Bundle-Match',
        cross_sell: 'Nerd-Empfehlung',
        upsell: 'Nerd-Upgrade',
      };
      return labels[String(item.kind || '').toLowerCase()] || 'Von uns ausgewählt';
    }

    defaultReason(item) {
      if (item.recommendationClass === 'copurchase') {
        return 'Wurde in mehreren Bestellungen gemeinsam gekauft.';
      }
      if (item.kind === 'upsell') return 'Von uns als Upgrade für dieses Setup ausgewählt.';
      if (item.kind === 'bundle') return 'Von uns als passendes Bundle geschnürt.';
      return 'Von unseren Nerds als passendes Add-on ausgewählt.';
    }

    formatPrice(item) {
      const cents = this.itemPriceCents(item);
      if (!Number.isFinite(cents)) return '';

      return this.formatCents(cents);
    }

    formatCents(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: this.dataset.currencyCode || 'EUR',
      }).format(Math.max(0, Math.round(Number(cents) || 0)) / 100);
    }

    getCartUpdateSections(cartItems) {
      const isCartPage = this.dataset.context === 'page' && cartItems;
      return isCartPage && typeof cartItems.getSectionsToRender === 'function'
        ? cartItems.getSectionsToRender().map((section) => section.section)
        : ['cart-drawer', 'cart-icon-bubble'];
    }

    async replaceCartLineWithBundle(offer, button) {
      if (button.disabled) return;

      const variantId = normalizeShopifyId(offer?.variantId);
      const replacementLineKey = String(offer?.replacementLineKey || '');
      const status = this.querySelector('.nt-cart-merchandising__status');
      const cartItems = document.querySelector('cart-items');
      const requestedSections = this.getCartUpdateSections(cartItems);
      if (!variantId || !replacementLineKey) return;

      button.disabled = true;
      button.textContent = 'Wird getauscht …';
      if (status) status.textContent = `${String(offer.title || 'Bundle')} wird als Bundle getauscht.`;

      try {
        const cartResponse = await fetch('/cart.js', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const cart = await cartResponse.json();
        const sourceLine = Array.isArray(cart?.items)
          ? cart.items.find((item) => String(item?.key || '') === replacementLineKey)
          : null;
        if (!cartResponse.ok || !sourceLine || Number(sourceLine.quantity) < 1) {
          throw new Error('Der Artikel im Warenkorb wurde geändert. Bitte Seite aktualisieren.');
        }

        const addResponse = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
        const addData = await addResponse.json();
        if (!addResponse.ok || addData.status) {
          throw new Error(addData.description || addData.message || 'Bundle konnte nicht hinzugefügt werden.');
        }

        const changeResponse = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: replacementLineKey,
            quantity: Math.max(0, Number(sourceLine.quantity) - 1),
            sections: requestedSections,
            sections_url: window.location.pathname,
          }),
        });
        const cartData = await changeResponse.json();
        if (!changeResponse.ok || cartData.status) {
          throw new Error(
            cartData.description
            || 'Bundle wurde hinzugefügt, der Einzelartikel aber nicht entfernt. Bitte Warenkorb aktualisieren.'
          );
        }

        window.location.assign(window.routes?.cart_url || '/cart');
        return;
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Erneut versuchen';
        if (status) status.textContent = error.message || 'Bundle konnte nicht getauscht werden.';
      }
    }

    async renderCartUpdate(cartData, variantId) {
      const cartItems = document.querySelector('cart-items');
      const isCartPage = this.dataset.context === 'page' && cartItems;

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'nerdtaverne-cart-merchandising',
          origin: 'nerdtaverne-cart-merchandising',
          productVariantId: variantId,
          cartData,
        });
        const focusScope = this.dataset.context === 'drawer'
          ? document.querySelector('#CartDrawer')
          : document.querySelector('cart-items');
        const focusTarget = focusScope?.querySelector(
          `[data-quantity-variant-id="${variantId}"]`
        ) || (this.dataset.context === 'drawer'
          ? document.querySelector('#CartDrawer .drawer__close')
          : document.querySelector('#main-cart-items'));
        const completionStatus = focusScope?.querySelector(
          '.nt-cart-merchandising__status'
        );
        if (completionStatus) {
          completionStatus.textContent = 'Warenkorb wurde aktualisiert.';
        }
        if (focusTarget instanceof HTMLElement) {
          if (!focusTarget.matches('button, a, input, select, textarea, [tabindex]')) {
            focusTarget.setAttribute('tabindex', '-1');
          }
          focusTarget.focus({ preventScroll: true });
        }
      } else if (isCartPage && typeof cartItems.getSectionInnerHTML === 'function') {
        cartItems.getSectionsToRender().forEach((section) => {
          const sectionHtml = cartData.sections?.[section.section];
          const sectionRoot = document.getElementById(section.id);
          if (!sectionHtml || !sectionRoot) return;
          const target = sectionRoot.querySelector(section.selector) || sectionRoot;
          target.innerHTML = cartItems.getSectionInnerHTML(sectionHtml, section.selector);
        });
      } else {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer
            || typeof cartDrawer.renderContents !== 'function'
            || !cartData.sections?.['cart-drawer']) {
          window.location.assign(window.routes?.cart_url || '/cart');
          return;
        }
        cartData.id = variantId;
        cartDrawer.renderContents(cartData);
      }
    }

    async addToCart(item, button) {
      if (button.disabled) return;

      const variantId = normalizeShopifyId(item.variantId);
      const status = this.querySelector('.nt-cart-merchandising__status');
      const cartItems = document.querySelector('cart-items');
      const requestedSections = this.getCartUpdateSections(cartItems);
      button.disabled = true;
      button.textContent = 'Wird ausgerüstet …';
      if (status) status.textContent = `${String(item.title || 'Artikel')} wird hinzugefügt.`;

      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: requestedSections,
            sections_url: window.location.pathname,
          }),
        });
        const cartData = await response.json();

        if (!response.ok || cartData.status) {
          throw new Error(cartData.description || cartData.message || 'Artikel konnte nicht hinzugefügt werden.');
        }

        await this.renderCartUpdate(cartData, variantId);
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Erneut versuchen';
        if (status) status.textContent = error.message || 'Artikel konnte nicht hinzugefügt werden.';
      }
    }
  }

  customElements.define('nerdtaverne-cart-merchandising', NerdtaverneCartMerchandising);
})();
